import { useHistory } from 'react-router';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { Modal, Button as BButton, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AddressInput } from '../../components/AddressInput/AddressInput';
import { Alert } from '../../components/Alert/Alert';
import { BuildingType, BuildingTypes, Item, PackageType, PackageTypes, ContentType, ContentTypes, Status, ValidationErrors } from '../../types';
import { Button } from '../../components/Button/Button';
import { extractError, track } from '../../util';
import { H1 } from '../../components/H1/H1';
import { Header } from '../../components/Header/Header';
import { Input } from '../../components/Input/Input';
import { CustomInputGroup } from '../../components/Input/CustomInputGroup';
import { OverlaySpinner } from '../../components/OverlaySpinner/OverlaySpinner';
import { RadioGroup } from '../../components/RadioGroup/RadioGroup';
import { Select } from '../../components/Select/Select';
import { Spinner } from '../../components/Spinner/Spinner';
import { useStore } from '../../store';
import * as api from '../../services/api';
import styles from './PackageDetails.module.scss';
import { BRAND_COLORS } from '../../config';
import Switch from 'react-switch';
import { InfoCircle } from '../../icons/InfoCircle/InfoCircle';
import moment from 'moment';
import 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import { renderContentTypePreview } from '../../components/Select/ContentTypePreview';

type PatchPackageDetails = { pickupPostcode: string; destinationPostcode: string };

// Body content for the "Tail Lift" info modal shown when the info icon next to
// the Tail Lift switch is clicked.
const TAIL_LIFT_INFO = (
  <>
    <p style={{ marginBottom: '12px' }}>Turn on Tail Lift if any item weighs over 30kg (or 25kg or more with some couriers) and there's no forklift or loading dock at pickup.</p>
    <p style={{ marginBottom: '12px' }}>A tail lift truck has a hydraulic platform that safely raises heavy goods onto the truck. Drivers can't hand-lift heavy items on their own.</p>
    <p style={{ marginBottom: '16px' }}>You can leave it off if you'll have a forklift or enough people on site to help load.</p>
    <div style={{ display: 'flex', gap: '10px', padding: '12px', borderRadius: '6px', background: '#fff8e6', color: '#7b341e' }}>
      <span>⚠️</span>
      <span>If a tail lift is needed but not selected, an extra charge may apply after booking. The pickup may also fail and need to be rebooked.</span>
    </div>
  </>
);

export function PackageDetails() {
  const orderId = useStore((state) => state.orderId);
  const pickupAddress1 = useStore((state) => state.pickupAddress1);
  const pickupAddress2 = useStore((state) => state.pickupAddress2);
  const pickupSuburb = useStore((state) => state.pickupSuburb);
  const pickupState = useStore((state) => state.pickupState);
  const pickupPostcode = useStore((state) => state.pickupPostcode);
  const pickupBuildingType = useStore((state) => state.pickupBuildingType);
  const pickupTailLift = useStore((state) => state.pickupTailLift);
  const destinationAddress1 = useStore((state) => state.destinationAddress1);
  const destinationAddress2 = useStore((state) => state.destinationAddress2);
  const destinationSuburb = useStore((state) => state.destinationSuburb);
  const destinationState = useStore((state) => state.destinationState);
  const destinationPostcode = useStore((state) => state.destinationPostcode);
  const destinationBuildingType = useStore((state) => state.destinationBuildingType);
  const dropOffTailLift = useStore((state) => state.dropOffTailLift);
  const dropOffPOBox = useStore((state) => state.dropOffPOBox);
  const items = useStore((state) => state.items);
  const reCreateOrder = useStore((state) => state.reCreateOrder);
  const customOrder = useStore((state) => state.customOrder);
  const lockOfficialQuote = useStore((state) => state.lockOfficialQuote);
  const setOrderId = useStore((state) => state.setOrderId);
  const setCurrentStep = useStore((state) => state.setCurrentStep);
  const setPickupSuburb = useStore((state) => state.setPickupSuburb);
  const setPickupPostcode = useStore((state) => state.setPickupPostcode);
  const setPickupState = useStore((state) => state.setPickupState);
  const setPickupBuildingType = useStore((state) => state.setPickupBuildingType);
  const setPickupTailLift = useStore((state) => state.setPickupTailLift);
  const setDestinationSuburb = useStore((state) => state.setDestinationSuburb);
  const setDestinationPostcode = useStore((state) => state.setDestinationPostcode);
  const setDestinationState = useStore((state) => state.setDestinationState);
  const setDestinationBuildingType = useStore((state) => state.setDestinationBuildingType);
  const setDropOffTailLift = useStore((state) => state.setDropOffTailLift);
  const setDropOffPOBox = useStore((state) => state.setDropOffPOBox);
  const setReCreateOrder = useStore((state) => state.setReCreateOrder);
  const editItem = useStore((state) => state.editItem);
  const addItem = useStore((state) => state.addItem);
  const removeItem = useStore((state) => state.removeItem);
  const clearEmptyItems = useStore((state) => state.clearEmptyItems);
  const setCustomOrder = useStore((state) => state.setCustomOrder);
  const setQuotes = useStore((state) => state.setQuotes);
  const setProgressBarPercentage = useStore((state) => state.setProgressBarPercentage);
  const [show, setShow] = useState(false);
  const [modalTitle, setModalTitle] = useState<ReactNode>('test');
  const [modalBody, setModalBody] = useState<ReactNode>('test');
  const handleClose = () => setShow(false);


  const history = useHistory();
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const itemError = extractError(errors, 'items');
  const [dimensionErrors, setDimensionErrors] = useState<{ [key: string]: string }>({});

  // Function to validate individual dimension field and update error messages
  const validateDimensionField = (itemIndex: number, fieldName: string, value: string | number) => {
    const fieldKey = `${itemIndex}-${fieldName}`;
    const numValue = parseFloat(String(value));

    // Clear previous error for this field
    setDimensionErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      return newErrors;
    });

    // Check if value is empty, null, or undefined
    if (value === null || value === undefined || value === '' || value === 0) {
      setDimensionErrors(prev => ({
        ...prev,
        [fieldKey]: 'This field is required'
      }));
      return;
    }

    // Check if value is a valid number
    if (isNaN(numValue)) {
      setDimensionErrors(prev => ({
        ...prev,
        [fieldKey]: 'Please enter a valid number'
      }));
      return;
    }

    // Check dimension limits based on field type
    if (fieldName === 'length' || fieldName === 'width' || fieldName === 'height') {
      if (numValue > 999) {
        setDimensionErrors(prev => ({
          ...prev,
          [fieldKey]: 'Max 999 CM allowed'
        }));
      } else if (numValue < 1) {
        setDimensionErrors(prev => ({
          ...prev,
          [fieldKey]: 'Min 1 CM required'
        }));
      }
    } else if (fieldName === 'weight') {
      if (numValue <= 0) {
        setDimensionErrors(prev => ({
          ...prev,
          [fieldKey]: 'Weight must be greater than 0'
        }));
      } else if (numValue > 99999.9) {
        setDimensionErrors(prev => ({
          ...prev,
          [fieldKey]: 'Max 99999.9 kg allowed'
        }));
      }
    } else if (fieldName === 'quantity') {
      if (numValue <= 0) {
        setDimensionErrors(prev => ({
          ...prev,
          [fieldKey]: 'Quantity must be at least 1'
        }));
      } else if (numValue > 99) {
        setDimensionErrors(prev => ({
          ...prev,
          [fieldKey]: 'Max 99 items allowed'
        }));
      }
    }
  };

  // Validation function to check if Get Quotes button should be disabled
  const isGetQuotesDisabled = () => {
    // Check if pickup and delivery addresses are filled
    if (!pickupPostcode || !destinationPostcode ||
      !pickupBuildingType || !destinationBuildingType) {
      return true;
    }

    // Check if all items have valid dimensions
    for (const item of items) {
      // Check if any dimension field is empty or invalid
      if (!item.length || !item.width || !item.height ||
        !item.weight || !item.quantity || !item.contents) {
        return true;
      }

      // Check if dimensions are valid numbers and within range
      const length = parseFloat(String(item.length));
      const width = parseFloat(String(item.width));
      const height = parseFloat(String(item.height));
      const weight = parseFloat(String(item.weight));
      const quantity = item.quantity; // quantity is already a number

      if (isNaN(length) || isNaN(width) || isNaN(height) ||
        isNaN(weight) || isNaN(quantity)) {
        return true;
      }

      // Check dimension limits
      if (length > 999 || width > 999 || height > 999 ||
        length <= 0 || width <= 0 || height <= 0 ||
        weight <= 0 || weight > 1000 || quantity <= 0) {
        return true;
      }
    }

    return false;
  };

  const handleShow = (title: ReactNode, body: ReactNode) => {
    setModalTitle(title);
    setModalBody(body);
    setShow(true);
  };

  const save = () => {
    setQuotes([]);
    setProgressBarPercentage(0);
    (async () => {
      const payload = {
        pickupAddress1: pickupAddress1,
        pickupAddress2: pickupAddress2,
        pickupSuburb: pickupSuburb,
        pickupState: pickupState,
        pickupPostcode: pickupPostcode,
        pickupBuildingType: pickupBuildingType,
        isPickupHandLoad: false,
        isPickupTailLift: pickupTailLift,
        destinationAddress1: destinationAddress1,
        destinationAddress2: destinationAddress2,
        destinationSuburb: destinationSuburb,
        destinationState: destinationState,
        destinationPostcode: destinationPostcode,
        destinationBuildingType: destinationBuildingType,
        isDropOffHandLoad: false,
        isDropOffTailLift: dropOffTailLift,
        isDropOffPOBox: dropOffPOBox,
        items: items,
      };

      try {
        setStatus('in-progress');
        await api.csrf();
        console.log('set order start', orderId);

        let id: any;
        if (orderId) {
          id = orderId;
          if (reCreateOrder) {
            const reCreateOrderRecord = await api.post<any>(`/order/${orderId}/reCreateOrder`, {});
            id = reCreateOrderRecord?.id ?? orderId;
            setReCreateOrder(false);
          }
        } else {
          const draftOrder = await api.post<any>('/order', {
            order_request_timezone: moment.tz.guess()
          });
          console.log('set order draftOrder', draftOrder);
          id = draftOrder.id;
        }
        if (!id) {
          throw new Error('Unable to resolve order id');
        }
        setOrderId(id);
        const data: PatchPackageDetails = await api.patch(`/order/${id}/package-details`, payload);

        setPickupPostcode(data.pickupPostcode);
        setDestinationPostcode(data.destinationPostcode);
        clearEmptyItems();

        track('app-get-quote');
        history.push('/quotes-comparisons');
      } catch (e: any) {
        setStatus('error');
        setErrors(e?.response?.data?.errors ?? {});
        track('app-error-package-details', { payload });
      }
    })();
  };
  async function checkItems() {
    setStatus('in-progress');

    setTimeout(() => {
      let itemsLength = items.length;
      let counter = 0;
      items.forEach(async (item: any) => {
        if (item.length === "" || item.width === "" || item.height === "" || item.weight === "" || item.quantity === "") {
          setStatus('empty-package-dimensions-error');
          return false;
        }
        if (item.contents === "") {
          setStatus('empty-contents-error');
          return false;
        }
        if (item.length > 999 || item.width > 999 || item.height > 999) {
          setStatus('large-package-dimensions-error');
          return false;
        }
        if (item.length === 0 || item.width === 0 || item.height === 0) {
          setStatus('small-package-dimensions-error');
          return false;
        }
        counter += 1;

      });
      if (itemsLength === counter) {
        save()
      }
    }, 1000);

  }

  useEffect(() => {
    // Official Quote bookings are locked to shipment-details; bounce back-nav here.
    if (lockOfficialQuote) {
      history.replace('/shipment-details');
      return;
    }
    setCurrentStep(0);
    if (customOrder) {
      setCustomOrder(false);
    }
  }, [setCurrentStep, setCustomOrder, lockOfficialQuote, history]);

  return (
    <>
      <Header />
      <OverlaySpinner enabled={status === 'in-progress'} />
      <div className={styles.page}>
        <section className={styles.card}>
          <div className={styles.cardRow}>
            <Modal
              show={show}
              onHide={handleClose}
              animation={true}
              aria-labelledby="contained-modal-title-vcenter"
              // Render inside the .FastCourierApp wrapper (instead of the default
              // document.body portal) so the app's scoped styles still apply.
              container={() => document.querySelector<HTMLElement>('.FastCourierApp')}
              centered
            >

              <Modal.Header>
                <Modal.Title>{modalTitle}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {modalBody}
              </Modal.Body>
              <Modal.Footer>
                <BButton
                  onClick={handleClose}
                  style={{ backgroundColor: BRAND_COLORS.primary, boxShadow: 'none', outline: 'none', border: 'none' }}
                >
                  Close
                </BButton>
              </Modal.Footer>
            </Modal>
            <div className={styles.column}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionLabelIcon}>
                  <FontAwesomeIcon icon={faLocationDot} />
                </span>
                Pickup
              </div>
              <div className={styles.sectionCard}>
                <div className={styles.addressBlock}>
                  <AddressInput
                    enableLookup
                    errors={errors}
                    label="From"
                    layout="vertical"
                    name="pickupAddress1"
                    onPostcodeChange={setPickupPostcode}
                    onStateChange={setPickupState}
                    onSuburbChange={setPickupSuburb}
                    postcode={pickupPostcode}
                    state={pickupState}
                    suburb={pickupSuburb}
                    placeholder="Search for postcode or suburb"
                  />
                </div>
                <RadioGroup<BuildingType>
                  className={styles.input}
                  label="Building type"
                  name="pickupBuildingType"
                  onChange={setPickupBuildingType}
                  options={BuildingTypes}
                  value={pickupBuildingType}
                />
                <div className={classNames(styles.optionGroup, styles.pickupOptionGroup)}>
                  <p className={styles.optionHeading}>Pick up options</p>
                  <label className={classNames(styles.toggleRow, styles.toggleRowSpaced)}>
                    <Switch
                      className="react-switch"
                      name="pickupTailLift"
                      onChange={setPickupTailLift}
                      checked={pickupTailLift}
                      onColor={BRAND_COLORS.primary}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      handleDiameter={16}
                      height={24}
                      width={42}
                    />
                    <span className={styles.switchLabel}>
                      Tail Lift
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleShow('Tail Lift', TAIL_LIFT_INFO);
                        }}
                        className={styles.info}
                      >
                        <InfoCircle className={styles.icon} />
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className={styles.column}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionLabelIcon}>
                  <FontAwesomeIcon icon={faLocationDot} />
                </span>
                Delivery
              </div>
              <div className={styles.sectionCard}>
                <div className={styles.addressBlock}>
                  <AddressInput
                    errors={errors}
                    label="To"
                    layout="vertical"
                    name="destinationAddress1"
                    onPostcodeChange={setDestinationPostcode}
                    onStateChange={setDestinationState}
                    onSuburbChange={setDestinationSuburb}
                    postcode={destinationPostcode}
                    state={destinationState}
                    suburb={destinationSuburb}
                    placeholder="Search for postcode or suburb"
                  />
                </div>
                <RadioGroup<BuildingType>
                  className={classNames(styles.input, styles.inputExtra)}
                  label="Building type"
                  name="desintationBuildingType"
                  onChange={setDestinationBuildingType}
                  options={BuildingTypes}
                  value={destinationBuildingType}
                />
                <div className={classNames(styles.optionGroup, styles.deliveryOptionGroup)}>
                  <p className={styles.optionHeading}>Delivery options</p>
                  <label className={styles.toggleRow}>
                    <Switch
                      className="react-switch"
                      name="dropOffTailLift"
                      onChange={setDropOffTailLift}
                      checked={dropOffTailLift}
                      onColor={BRAND_COLORS.primary}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      handleDiameter={16}
                      height={24}
                      width={42}
                    />
                    <span className={styles.switchLabel}>
                      Tail Lift
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleShow('Tail Lift', TAIL_LIFT_INFO);
                        }}
                        className={styles.info}
                      >
                        <InfoCircle className={styles.icon} />
                      </span>
                    </span>
                  </label>
                  <label className={classNames(styles.toggleRow, styles.toggleBottomSpace)}>
                    <Switch
                      className="react-switch"
                      name="dropOffPOBox"
                      onChange={setDropOffPOBox}
                      checked={dropOffPOBox}
                      onColor={BRAND_COLORS.primary}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      handleDiameter={16}
                      height={24}
                      width={42}
                    />
                    <span className={styles.switchLabel}>
                      PO Box
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleShow('PO Box', 'Only StarTrack & Auspost deliver to PO Boxes so you will only be served with quotes from these carriers. All other carriers only deliver to residential/commerical full addresses.');
                        }}
                        className={styles.info}
                      >
                        <InfoCircle className={styles.icon} />
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.packageBlock}>
            <H1 className={styles.sectionHeading} alignment="left">
              <span className={styles.sectionHeadingContent}>
                <span className={styles.sectionHeadingIcon}>
                  <FontAwesomeIcon icon={faBox} />
                </span>
                Package Details
              </span>
            </H1>
            <div className={styles.items}>
              {items.map((item, i) => (
                <div className={styles.item} key={i}>
                  <div className={styles.itemRow}>
                    <div className={styles.cell}>
                      <Select<PackageType>
                        className={styles.simpleInput}
                        label="Package Type"
                        onChange={(value) => editItem(i, 'type', value)}
                        options={PackageTypes}
                        value={item.type}
                      />
                    </div>
                    <div className={styles.cell}>
                      <Input<Item[keyof Item]>
                        className={styles.simpleInput}
                        label="Weight (kgs)"
                        name="weight"
                        type="number"
                        errors={{}}
                        value={item.weight}
                        onChange={(value) => {
                          editItem(i, 'weight', value);
                          validateDimensionField(i, 'weight', value);
                        }}
                      />
                      {dimensionErrors[`${i}-weight`] && (
                        <div className={styles.error} style={{ fontSize: '12px', color: 'red', marginTop: '4px' }}>
                          {dimensionErrors[`${i}-weight`]}
                        </div>
                      )}
                    </div>
                    <div className={styles.cell}>
                      <CustomInputGroup<Item[keyof Item]>
                        label="Length"
                        name="length"
                        type="number"
                        errors={{}}
                        min={1}
                        value={item.calculatedLength}
                        calculatedValue={item.length}
                        onChange={(value, calculatedValue) => {
                          editItem(i, 'length', calculatedValue);
                          editItem(i, 'calculatedLength', value);
                          validateDimensionField(i, 'length', calculatedValue);
                        }}
                      />
                      {dimensionErrors[`${i}-length`] && (
                        <div className={styles.error} style={{ fontSize: '12px', color: 'red', marginTop: '4px' }}>
                          {dimensionErrors[`${i}-length`]}
                        </div>
                      )}
                    </div>
                    <div className={styles.cell}>
                      <CustomInputGroup<Item[keyof Item]>
                        label="Width"
                        name="width"
                        type="number"
                        errors={{}}
                        min={1}
                        value={item.calculatedWidth}
                        calculatedValue={item.width}
                        onChange={(value, calculatedValue) => {
                          editItem(i, 'width', calculatedValue);
                          editItem(i, 'calculatedWidth', value);
                          validateDimensionField(i, 'width', calculatedValue);
                        }}
                      />
                      {dimensionErrors[`${i}-width`] && (
                        <div className={styles.error} style={{ fontSize: '12px', color: 'red', marginTop: '4px' }}>
                          {dimensionErrors[`${i}-width`]}
                        </div>
                      )}
                    </div>
                    <div className={styles.cell}>
                      <CustomInputGroup<Item[keyof Item]>
                        label="Height"
                        name="height"
                        type="number"
                        errors={{}}
                        min={1}
                        value={item.calculatedHeight}
                        calculatedValue={item.height}
                        onChange={(value, calculatedValue) => {
                          editItem(i, 'height', calculatedValue);
                          editItem(i, 'calculatedHeight', value);
                          validateDimensionField(i, 'height', calculatedValue);
                        }}
                      />
                      {dimensionErrors[`${i}-height`] && (
                        <div className={styles.error} style={{ fontSize: '12px', color: 'red', marginTop: '4px' }}>
                          {dimensionErrors[`${i}-height`]}
                        </div>
                      )}
                    </div>
                    <div className={styles.cell}>
                      <Spinner
                        value={item.quantity || 1}
                        onChange={(value) => {
                          const numValue = parseInt(String(value)) || 1;
                          editItem(i, 'quantity', numValue);
                          validateDimensionField(i, 'quantity', numValue);
                        }}
                        min={1}
                        max={99}
                      >
                        <Input<Item[keyof Item]>
                          className={styles.simpleInput}
                          label="Quantity"
                          name="quantity"
                          type="number"
                          errors={{}}
                          min={1}
                          max={99}
                          value={item.quantity || ''}
                          onChange={(value) => {
                            const numValue = parseInt(String(value)) || 0;
                            if (numValue >= 1 && numValue <= 99) {
                              editItem(i, 'quantity', numValue);
                              validateDimensionField(i, 'quantity', numValue);
                            }
                          }}
                        />
                      </Spinner>
                      {dimensionErrors[`${i}-quantity`] && (
                        <div className={styles.error} style={{ fontSize: '12px', color: 'red', marginTop: '4px' }}>
                          {dimensionErrors[`${i}-quantity`]}
                        </div>
                      )}
                    </div>
                    <div className={styles.cell}>
                      <Select<ContentType>
                        className={styles.simpleInput}
                        label="Content Type"
                        onChange={(value) => editItem(i, 'contents', value)}
                        options={ContentTypes}
                        value={item.contents}
                        richPreview={renderContentTypePreview}
                      />
                    </div>
                  </div>
                  {items.length > 1 && (
                    <button className={styles.remove} onClick={() => removeItem(i)}>
                      Remove package
                    </button>
                  )}
                </div>
              ))}
              <div className={styles.addToolbar}>
                <Button type="ghost" size="medium" disabled={items.length >= 10} onClick={addItem}>
                  Add package
                </Button>
              </div>
            </div>
          </div>
          <div className={styles.toolbar}>
            <Button
              className={styles.primaryCta}
              // disabled={isGetQuotesDisabled()}
              type="primary"
              onClick={checkItems}
            >
              Get Quotes
            </Button>
          </div>
        </section>
        {status === 'error' && (
          <Alert type="error" className={styles.alert}>
            There is an error submitting your details. Please check the fields above. It seems you have not entered correct dimensions.
            <br />
            {itemError}
          </Alert>
        )}
        {status === 'large-package-dimensions-error' && (
          <Alert type="error" className={styles.alert}>
            Max 999 CM, please check if your <b>dimension</b> measurement is in CM or MM
            <br />
            {itemError}
          </Alert>
        )}
        {status === 'small-package-dimensions-error' && (
          <Alert type="error" className={styles.alert}>
            Min 5 MM, please check if your <b>dimension</b> measurement is correct
            <br />
            {itemError}
          </Alert>
        )}
        {status === 'empty-package-dimensions-error' && (
          <Alert type="error" className={styles.alert}>
            Dimensions should not be empty!
            <br />
            {itemError}
          </Alert>
        )}
        {status === 'empty-contents-error' && (
          <Alert type="error" className={styles.alert}>
            Contents is required!
            <br />
            {itemError}
          </Alert>
        )}
      </div>
    </>
  );
}
